import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { Upload, Save } from "lucide-react";
import { api, mediaUrl, formatApiErrorDetail } from "@/lib/api";
import { ASSETS } from "@/assets";

export default function CartSettingsManager() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => { api.get("/admin/settings").then((res) => setForm(res.data)).catch(() => {}); }, []);

  if (!form) return <p className="text-sc-espresso/50">Loading…</p>;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const pickOnMap = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    setForm({
      ...form,
      map_cart_x: Math.round(((e.clientX - r.left) / r.width) * 100),
      map_cart_y: Math.round(((e.clientY - r.top) / r.height) * 100),
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/settings", form);
      setForm(data);
      toast.success("Settings saved");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/upload-photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, cart_photo_url: data.url }));
      toast.success("Photo updated");
    } catch (err) { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };

  const photo = form.cart_photo_url ? mediaUrl(form.cart_photo_url) : ASSETS.cartPhoto;

  return (
    <div data-testid="admin-settings" className="max-w-3xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-serif-display text-3xl text-sc-espresso">Cart & Map</h2>
          <p className="text-sm text-sc-espresso/60 mt-1">Update the live cart position, today's info, and photo.</p>
        </div>
        <button onClick={save} disabled={saving} data-testid="save-settings-btn" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-sc-espresso text-sc-cream text-sm font-medium hover:bg-sc-gold transition-colors disabled:opacity-60">
          <Save size={18} /> {saving ? "Saving…" : "Save Changes"}
        </button>
      </div>

      <div className="space-y-6">
        <div className="bg-white rounded-2xl p-6 border border-sc-gold/15">
          <h3 className="font-serif-display text-xl text-sc-espresso mb-4">Live cart location (the map pin)</h3>
          <div className="relative rounded-xl overflow-hidden border border-sc-gold/25 cursor-crosshair max-w-[280px] mx-auto" onClick={pickOnMap} data-testid="settings-map-picker">
            <img src={ASSETS.mangaMap} alt="map" className="w-full" style={{ aspectRatio: "848/1264", objectFit: "cover" }} draggable={false} />
            <img src={ASSETS.cartIcon} alt="cart" className="absolute -translate-x-1/2 -translate-y-1/2 w-12 drop-shadow" style={{ left: `${form.map_cart_x}%`, top: `${form.map_cart_y}%` }} draggable={false} />
          </div>
          <p className="text-xs text-sc-espresso/50 mt-1">Click the map to move the cart. x: {form.map_cart_x}% · y: {form.map_cart_y}%</p>
        </div>

        <div className="bg-white rounded-2xl p-6 border border-sc-gold/15 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Current location"><input value={form.current_location} onChange={set("current_location")} data-testid="current-location-input" className={inputCls} /></Field>
          <Field label="Today's hours"><input value={form.current_time} onChange={set("current_time")} className={inputCls} /></Field>
          <div className="sm:col-span-2"><Field label="Special note"><input value={form.current_notes} onChange={set("current_notes")} className={inputCls} /></Field></div>
        </div>

        <div className="bg-white rounded-2xl p-6 border border-sc-gold/15 space-y-4">
          <Field label="Promo tagline"><textarea rows={2} value={form.tagline} onChange={set("tagline")} className={inputCls + " resize-none"} /></Field>
          <Field label="Instagram URL"><input value={form.instagram_url} onChange={set("instagram_url")} className={inputCls} /></Field>
        </div>

        <div className="bg-white rounded-2xl p-6 border border-sc-gold/15">
          <h3 className="font-serif-display text-xl text-sc-espresso mb-4">Cart photo</h3>
          <div className="flex flex-col sm:flex-row gap-5 items-start">
            <img src={photo} alt="cart" className="w-48 rounded-2xl object-cover border" style={{ aspectRatio: "4/3" }} />
            <div>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={upload} data-testid="photo-upload-input" />
              <button onClick={() => fileRef.current?.click()} disabled={uploading} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-sc-espresso text-sc-espresso text-sm font-medium hover:bg-sc-espresso hover:text-sc-cream transition-colors disabled:opacity-60">
                <Upload size={18} /> {uploading ? "Uploading…" : "Upload new photo"}
              </button>
              <p className="text-xs text-sc-espresso/50 mt-2">JPG / PNG / WebP. Shows on the homepage promo section.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const inputCls = "w-full bg-white border border-sc-gold/25 rounded-xl px-4 py-2.5 text-sc-espresso focus:outline-none focus:border-sc-gold";
function Field({ label, children }) {
  return (<div><label className="eyebrow block mb-1.5">{label}</label>{children}</div>);
}
